import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface AddMembersFormState {
	// Step 1: Basic Info
	basicInfo: {
		firstName: string;
		lastName: string;
		emailAddress: string;
		phoneNumber: string;
		technology: number | null;
		memberType: string | number | null;
		dob: string;
		cnic: string;
		designation: number | null;
		qualification: string | number;
		techQualification: string | number;
		dateOfJoining: string;
		grade: number | null;
		address: string;
		lat: number | null;
		long: number | null;
		organizationType: string | number;
	};

	// Step 2: Current Posting
	currentPosting: {
		province: string | number;
		division: string | number;
		district: string | number;
		tehsil: string | number;
		city: string | number;
		unionCouncil: string | number;
		instituteName: string;
		grade: string | number;
		dateOfJoining: string;
		designation: string | number;
		technologies: (string | number)[];
	};

	// Step 3: Reporting Officer
	reportingOfficer: {
		reportingOfficerFullName: string;
		reportingOfficerDesignation: string | number;
		reportingOfficerEmail: string;
		reportingOfficerPhoneNumber: string;
	};

	// Step 4: Bank Details
	bankDetails: {
		hasBopAccount: boolean;
		bankName: string;
		accountTitle: string;
		bankAccountNumber: string;
		bankBranchCode: string;
		iban: string;
	};

	// Step 5: Attachments
	attachments: {
		attachment: string;
		fileInfo: {
			name: string;
			size: number;
			type: string;
		} | null;
	};

	// UI State
	currentStep: number;
	isFormValid: boolean;
}

const initialState: AddMembersFormState = {
	basicInfo: {
		firstName: "",
		lastName: "",
		emailAddress: "",
		phoneNumber: "",
		technology: null,
		memberType: null,
		designation: null,
		qualification: "",
		techQualification: "",
		dateOfJoining: "",
		dob: "",
		cnic: "",
		grade: null,
		address: "",
		lat: null,
		long: null,
		organizationType: "",
	},
	currentPosting: {
		province: "",
		division: "",
		district: "",
		tehsil: "",
		city: "",
		unionCouncil: "",
		instituteName: "",
		grade: "",
		dateOfJoining: "",
		designation: "",
		technologies: [],
	},
	reportingOfficer: {
		reportingOfficerFullName: "",
		reportingOfficerDesignation: "",
		reportingOfficerEmail: "",
		reportingOfficerPhoneNumber: "",
	},
	bankDetails: {
		hasBopAccount: false,
		bankName: "",
		accountTitle: "",
		bankAccountNumber: "",
		bankBranchCode: "",
		iban: "",
	},
	attachments: {
		attachment: "",
		fileInfo: null,
	},
	currentStep: 0,
	isFormValid: false,
};

const addMembersSlice = createSlice({
	name: "addMembers",
	initialState,
	reducers: {
		// Update specific step data
		updateBasicInfo: (state, action: PayloadAction<Partial<AddMembersFormState["basicInfo"]>>) => {
			state.basicInfo = { ...state.basicInfo, ...action.payload };
		},
		updateCurrentPosting: (state, action: PayloadAction<Partial<AddMembersFormState["currentPosting"]>>) => {
			state.currentPosting = { ...state.currentPosting, ...action.payload };
		},
		updateReportingOfficer: (state, action: PayloadAction<Partial<AddMembersFormState["reportingOfficer"]>>) => {
			state.reportingOfficer = { ...state.reportingOfficer, ...action.payload };
		},
		updateBankDetails: (state, action: PayloadAction<Partial<AddMembersFormState["bankDetails"]>>) => {
			state.bankDetails = { ...state.bankDetails, ...action.payload };
		},
		updateAttachments: (state, action: PayloadAction<Partial<AddMembersFormState["attachments"]>>) => {
			state.attachments = { ...state.attachments, ...action.payload };
		},

		// Navigation
		setCurrentStep: (state, action: PayloadAction<number>) => {
			state.currentStep = action.payload;
		},
		nextStep: state => {
			if (state.currentStep < 4) {
				state.currentStep += 1;
			}
		},
		previousStep: state => {
			if (state.currentStep > 0) {
				state.currentStep -= 1;
			}
		},

		// Form validation
		setFormValid: (state, action: PayloadAction<boolean>) => {
			state.isFormValid = action.payload;
		},

		// Reset form (when modal closes or form is submitted)
		resetForm: () => {
			return initialState;
		},

		// Reset specific step
		resetStep: (state, action: PayloadAction<keyof Omit<AddMembersFormState, "currentStep" | "isFormValid">>) => {
			const stepKey = action.payload;
			if (stepKey === "basicInfo") {
				state.basicInfo = initialState.basicInfo;
			} else if (stepKey === "currentPosting") {
				state.currentPosting = initialState.currentPosting;
			} else if (stepKey === "reportingOfficer") {
				state.reportingOfficer = initialState.reportingOfficer;
			} else if (stepKey === "bankDetails") {
				state.bankDetails = initialState.bankDetails;
			} else if (stepKey === "attachments") {
				state.attachments = initialState.attachments;
			}
		},
	},
});

export const {
	updateBasicInfo,
	updateCurrentPosting,
	updateReportingOfficer,
	updateBankDetails,
	updateAttachments,
	setCurrentStep,
	nextStep,
	previousStep,
	setFormValid,
	resetForm,
	resetStep,
} = addMembersSlice.actions;

export default addMembersSlice.reducer;
